import { useMemo, useEffect, useRef, useState } from "react";
import { Stack, ContextualMenu, IContextualMenuItem } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import styles from "./Answer.module.css";
import copyIcon from "../../assets/CopyIcon.png";
import exportIcon from "../../assets/ExportAnswer.png";
import showContentIcon from "../../assets/ShowContent.png";
import { ChatAppResponse, getCitationFilePath, SpeechConfig } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { exportRenderedAnswerToDocx } from "./answerDocxExport";
import { SpeechOutputBrowser } from "./SpeechOutputBrowser";
import { SpeechOutputAzure } from "./SpeechOutputAzure";
import { writeRenderedAnswerToPdf } from "./answerPdfExport";
import Markdown from "markdown-to-jsx";

interface Props {
    answer: ChatAppResponse;
    index: number;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
}

export const Answer = ({
    answer,
    index,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputAzure,
    showSpeechOutputBrowser
}: Props) => {
    const followupQuestions = answer.context?.followup_questions;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming, onCitationClicked), [answer]);
    const { t } = useTranslation();

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    // Debug logging
    // console.log("🔍 Sanitized HTML:", sanitizedAnswerHtml);
    const answerTextRef = useRef<HTMLDivElement>(null);

    // Add event listeners for citation clicks after render
    useEffect(() => {
        if (answerTextRef.current) {
            const citationLinks = answerTextRef.current.querySelectorAll("a.supContainer");

            const handleCitationClick = (event: Event) => {
                event.preventDefault();
                const target = event.currentTarget as HTMLAnchorElement;
                const citationText = target.getAttribute("title");
                if (citationText) {
                    const path = getCitationFilePath(citationText);
                    onCitationClicked(path);
                }
            };

            citationLinks.forEach(link => {
                link.addEventListener("click", handleCitationClick);
            });

            // Cleanup event listeners
            return () => {
                citationLinks.forEach(link => {
                    link.removeEventListener("click", handleCitationClick);
                });
            };
        }
    }, [sanitizedAnswerHtml, onCitationClicked]);
    const [copied, setCopied] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportButtonRef = useRef<HTMLDivElement>(null);

    const handleCopy = () => {
        // For markdown content, remove the citation markers but keep the text
        const textToCopy = sanitizedAnswerHtml.replace(/<a [^>]*><sup>\d+<\/sup><\/a>/g, "");

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    };

    const exportToPDF = async () => {
        try {
            const { jsPDF } = await import("jspdf");

            if (!answerTextRef.current) {
                throw new Error("Answer content not available for PDF export.");
            }

            const pdf = new jsPDF({ unit: "mm", format: "a4", compress: true });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            writeRenderedAnswerToPdf(pdf, answerTextRef.current, {
                margin: 15,
                bodyFontSize: 10,
                bodyLineMm: 4.6,
                pageWidth,
                pageHeight
            }, {
                citations: parsedAnswer.citations,
                citationSectionTitle: t("citationWithColon") || "Citations:"
            });

            pdf.save(`answer-${index + 1}.pdf`);
        } catch (error) {
            console.error("Failed to export PDF:", error);
            alert("Failed to export PDF. Please try again.");
        }
        setShowExportMenu(false);
    };

    const exportToWord = async () => {
        try {
            if (!answerTextRef.current) {
                throw new Error("Answer content not available for Word export.");
            }

            await exportRenderedAnswerToDocx(answerTextRef.current, {
                citations: parsedAnswer.citations,
                citationSectionTitle: t("citationWithColon") || "Citations:",
                fileName: `answer-${index + 1}.docx`
            });
        } catch (error) {
            console.error("Failed to export Word:", error);
            alert("Failed to export Word document. Please try again.");
        }
        setShowExportMenu(false);
    };

    const exportMenuItems: IContextualMenuItem[] = [
        {
            key: "pdf",
            text: "Export to PDF",
            iconProps: { iconName: "PDF" },
            onClick: () => {
                exportToPDF();
            }
        },
        {
            key: "word",
            text: "Export to Word",
            iconProps: { iconName: "WordDocument" },
            onClick: () => {
                exportToWord();
            }
        }
    ];

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item grow>
                <div ref={answerTextRef} className={styles.answerText}>
                    <Markdown>{sanitizedAnswerHtml}</Markdown>
                </div>
            </Stack.Item>

            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <div className={styles.citationsContainer}>
                        <span className={styles.citationLearnMore}>{t("citationWithColon")}</span>
                        {parsedAnswer.citations.map((x, i) => {
                            const path = getCitationFilePath(x);
                            return (
                                <a key={i} className={styles.citation} title={x} onClick={() => onCitationClicked(path)}>
                                    {`${i + 1}. ${x}`}
                                </a>
                            );
                        })}
                    </div>
                </Stack.Item>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>{t("followupQuestions")}</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            <Stack.Item>
                <div className={styles.actionButtonsRow}>
                    <div className={styles.actionButtonsLeft}>
                        <button
                            className={copied ? styles.actionButtonCopied : styles.actionButton}
                            title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            aria-label={copied ? t("tooltips.copied") : t("tooltips.copy")}
                            onClick={handleCopy}
                        >
                            <div className={styles.copyIconWrapper}>
                                <img src={copyIcon} alt="Copy" className={styles.actionIcon} />
                                {copied && <span className={styles.checkmarkOverlay}>✓</span>}
                            </div>
                        </button>
                        <div ref={exportButtonRef} style={{ display: "inline-block" }}>
                            <button
                                className={showExportMenu ? styles.actionButtonSelected : styles.actionButton}
                                title="Export answer"
                                aria-label="Export answer"
                                onClick={() => setShowExportMenu(true)}
                                disabled={isStreaming}
                            >
                                <img src={exportIcon} alt="Export" className={styles.actionIcon} />
                            </button>
                            {showExportMenu && <ContextualMenu items={exportMenuItems} target={exportButtonRef} onDismiss={() => setShowExportMenu(false)} />}
                        </div>
                        <button
                            className={isSelected ? styles.actionButtonSelected : styles.actionButton}
                            title={t("tooltips.showSupportingContent")}
                            aria-label={t("tooltips.showSupportingContent")}
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.context.data_points || isStreaming}
                        >
                            <img src={showContentIcon} alt="Show content" className={styles.actionIcon} />
                        </button>
                    </div>
                </div>
            </Stack.Item>
        </Stack>
    );
};
